/**
 * Reactive accessor for the provisioned user blob cached in localStorage
 * (`curaition_user`).
 *
 * `useAuth()` reads this blob synchronously on each render and is NOT reactive
 * to in-tab writes, so editing the display name on the Profile page would not
 * refresh the header/sidebar chips until a full reload — which looks broken.
 *
 * This module gives those surfaces a subscription: `setStoredUserFields()`
 * writes localStorage and notifies subscribers; `useStoredUser()` re-renders
 * the consuming component when the blob changes.
 */

import { useSyncExternalStore } from 'react';
import type { User } from '../../../../shared/types/api';

const STORAGE_KEY = 'curaition_user';
const listeners = new Set<() => void>();

// Cache the parsed snapshot keyed by the raw string so useSyncExternalStore
// receives a stable reference unless the stored value actually changed
// (returning a fresh object every call would loop the render).
let cache: { raw: string | null; value: User | null } = { raw: null, value: null };

export function getStoredUser(): User | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw !== cache.raw) {
    let value: User | null = null;
    if (raw) {
      try {
        value = JSON.parse(raw) as User;
      } catch {
        value = null;
      }
    }
    cache = { raw, value };
  }
  return cache.value;
}

/**
 * Merge `updates` into the stored user blob and notify subscribers. No-op if
 * there's no stored user (e.g. mid-sign-out).
 */
export function setStoredUserFields(updates: Partial<User>): void {
  const current = getStoredUser();
  if (!current) return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, ...updates }));
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Reactive read of the stored user. Re-renders on `setStoredUserFields`. */
export function useStoredUser(): User | null {
  return useSyncExternalStore(subscribe, getStoredUser, getStoredUser);
}
